import React, { useState, useEffect, useRef } from 'react';
import './ProductImageModal.css';
import { resolveImageUrl } from '../utils/image';

interface ProductImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  startIndex?: number;
  productName: string;
}

export default function ProductImageModal({ isOpen, onClose, images = [], startIndex = 0, productName }: ProductImageModalProps) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const imageRef = useRef<HTMLImageElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsLoading(true);
      setCurrentIndex(startIndex || 0);
    }
  }, [isOpen, startIndex, images]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentIndex, images]);

  const currentImage = (images && images.length > 0 && images[currentIndex]) ? images[currentIndex] : '';

  useEffect(() => {
    // reset loading when image changes
    setIsLoading(true);
  }, [currentImage]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const prevImage = () => {
    if (!images || images.length === 0) return;
    setCurrentIndex((currentIndex - 1 + images.length) % images.length);
    handleResetZoom();
  };

  const nextImage = () => {
    if (!images || images.length === 0) return;
    setCurrentIndex((currentIndex + 1) % images.length);
    handleResetZoom();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.5, Math.min(5, prev * delta)));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="product-image-modal" 
      ref={modalRef}
      onClick={handleBackdropClick}
    >
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="nav-arrow left" onClick={prevImage} aria-label="Previous image">&#8249;</button>
          <h3 className="modal-title" style={{ flex: 1, textAlign: 'center' }}>{productName}</h3>
          <button className="nav-arrow right" onClick={nextImage} aria-label="Next image">&#8250;</button>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Image Container */}
        <div className="image-container">
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
          {currentImage ? (
            <img
              ref={imageRef}
              src={resolveImageUrl(currentImage)}
              alt={productName}
              className={`modal-image ${isDragging ? 'dragging' : ''}`}
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
              }}
              onLoad={handleImageLoad}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={(e) => {
                e.stopPropagation();
                if (scale === 1) {
                  handleZoomIn();
                }
              }}
              onError={(e) => {
                setIsLoading(false);
                const t = e.currentTarget as HTMLImageElement;
                if (!t.src.startsWith('data:')) {
                  t.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect width="300" height="300" fill="%23f8fafc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="64">🧨</text></svg>';
                }
              }}
            />
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>No image available</div>
          )}
        </div>

        {/* Thumbnails */}
        {images && images.length > 0 && (
          <div className="modal-thumbnails" style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 8 }}>
            {images.map((img, idx) => (
              <img
                key={idx}
                src={resolveImageUrl(img)}
                alt={`thumb-${idx}`}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: idx === currentIndex ? '2px solid #1976d2' : '2px solid transparent', cursor: 'pointer' }}
                onClick={() => { setCurrentIndex(idx); setIsLoading(true); handleResetZoom(); }}
                onError={(e) => { const t = e.currentTarget as HTMLImageElement; t.style.display = 'none'; }}
              />
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="modal-controls">
          <button className="control-button" onClick={handleZoomOut} disabled={scale <= 0.5}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13H5v-2h14v2z"/>
            </svg>
            <span>Zoom Out</span>
          </button>
          
          <button className="control-button" onClick={handleResetZoom}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>Reset</span>
          </button>
          
          <button className="control-button" onClick={handleZoomIn} disabled={scale >= 5}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            <span>Zoom In</span>
          </button>
        </div>

        {/* Instructions */}
        <div className="modal-instructions">
          <p>Click image to zoom • Drag to pan • Scroll to zoom • ESC / ← / → to navigate • Click thumbnails to switch</p>
        </div>
      </div>
    </div>
  );
}
